import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Star, Clock, BookOpen, FileText, ShoppingCart, Heart, Play, 
  CheckCircle, Users, Award, ArrowLeft, Download, CreditCard
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [isPurchased, setIsPurchased] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [buyNowLoading, setBuyNowLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/products/${productId}`),
          axios.get(`${API}/reviews/${productId}`)
        ]);
        setProduct(productRes.data);
        setReviews(reviewsRes.data);

        // Check if purchased
        if (isAuthenticated) {
          try {
            const purchasesRes = await axios.get(`${API}/purchases`, {
              headers: getAuthHeaders(),
              withCredentials: true
            });
            const purchased = purchasesRes.data.some(p => p.product_id === productId);
            setIsPurchased(purchased);
          } catch (e) {
            console.error('Failed to check purchases');
          }
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Product not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productId, isAuthenticated, getAuthHeaders, navigate]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add items to cart');
      navigate('/auth');
      return;
    }

    const result = await addToCart(product.product_id, product.product_type);
    if (result.success) {
      toast.success('Added to cart!');
    } else {
      toast.error(result.error);
    }
  };

  const handleWishlist = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add to wishlist');
      return;
    }

    try {
      if (isInWishlist) {
        await axios.delete(`${API}/wishlist/${product.product_id}`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        setIsInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/${product.product_id}`, {}, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        setIsInWishlist(true);
        toast.success('Added to wishlist!');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const handleBuyNow = async () => {
    setBuyNowLoading(true);
    try {
      // Use guest checkout - no login required
      const response = await axios.post(`${API}/checkout/guest`, {
        product_id: product.product_id,
        product_type: product.product_type,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process purchase');
      setBuyNowLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewText.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await axios.post(`${API}/reviews`, {
        product_id: productId,
        rating: reviewRating,
        comment: reviewText
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setReviews([response.data, ...reviews]);
      setReviewText('');
      setReviewRating(5);
      toast.success('Review submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-[#E2E8F0] rounded mb-8"></div>
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="aspect-video bg-[#E2E8F0] rounded-xl"></div>
              <div className="space-y-4">
                <div className="h-10 bg-[#E2E8F0] rounded w-3/4"></div>
                <div className="h-6 bg-[#E2E8F0] rounded w-full"></div>
                <div className="h-6 bg-[#E2E8F0] rounded w-2/3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isCourse = product?.product_type === 'course';

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Back Button */}
        <Link 
          to={isCourse ? '/courses' : '/downloads'} 
          className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#2D4A5E] mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {isCourse ? 'Courses' : 'Downloads'}
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image/Video */}
          <div className="space-y-4">
            <div className="relative aspect-video rounded-xl overflow-hidden shadow-lg">
              {isCourse && product.video_url ? (
                <video
                  src={product.video_url}
                  poster={product.thumbnail}
                  controls
                  className="w-full h-full object-cover"
                  data-testid="product-video"
                />
              ) : (
                <img
                  src={product?.thumbnail || 'https://images.unsplash.com/photo-1634117622592-114e3024ff27?w=800'}
                  alt={product?.title}
                  className="w-full h-full object-cover"
                />
              )}
              {product?.is_featured && (
                <Badge className="absolute top-4 left-4 bg-[#D4AF37] text-[#1A202C]">
                  Featured
                </Badge>
              )}
            </div>

            {/* Lessons Preview (for courses) */}
            {isCourse && product?.lessons?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
                <h3 className="font-semibold text-[#1A202C] mb-4">Course Content</h3>
                <div className="space-y-3">
                  {product.lessons.map((lesson, index) => (
                    <div key={lesson.id} className="flex items-center justify-between py-2 border-b border-[#E2E8F0] last:border-0">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-[#F1F5F9] flex items-center justify-center text-sm text-[#64748B]">
                          {index + 1}
                        </span>
                        <span className="text-[#1A202C]">{lesson.title}</span>
                      </div>
                      <span className="text-sm text-[#64748B]">{lesson.duration}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              <Badge variant="outline" className="mb-3">
                {product?.category}
              </Badge>
              <h1 className="text-3xl lg:text-4xl font-bold text-[#1A202C] mb-4" data-testid="product-title">
                {product?.title}
              </h1>
              
              {/* Rating & Stats */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                {product?.review_count > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${star <= Math.round(product.avg_rating) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#E2E8F0]'}`}
                        />
                      ))}
                    </div>
                    <span className="font-medium">{product.avg_rating?.toFixed(1)}</span>
                    <span className="text-[#64748B]">({product.review_count} reviews)</span>
                  </div>
                )}
                {isCourse && product?.duration && (
                  <div className="flex items-center gap-2 text-[#64748B]">
                    <Clock className="w-4 h-4" />
                    {product.duration}
                  </div>
                )}
                {!isCourse && product?.file_type && (
                  <Badge variant="secondary">
                    <FileText className="w-3 h-3 mr-1" />
                    {product.file_type.toUpperCase()}
                  </Badge>
                )}
              </div>

              <p className="text-[#64748B] leading-relaxed">
                {product?.description}
              </p>
            </div>

            {/* Price & Actions */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-4xl font-bold text-[#2D4A5E]">
                    ${product?.price?.toFixed(2)}
                  </span>
                  <span className="text-[#64748B] ml-2">one-time</span>
                </div>
                <button
                  onClick={handleWishlist}
                  className={`p-3 rounded-full transition-all ${
                    isInWishlist 
                      ? 'bg-red-100 text-red-500' 
                      : 'bg-[#F1F5F9] text-[#64748B] hover:text-red-500'
                  }`}
                  data-testid="wishlist-button"
                >
                  <Heart className={`w-5 h-5 ${isInWishlist ? 'fill-current' : ''}`} />
                </button>
              </div>

              {isPurchased ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">You own this {isCourse ? 'course' : 'download'}</span>
                  </div>
                  {isCourse ? (
                    <Link to={`/course/${productId}/learn`}>
                      <Button className="w-full bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full py-6 text-lg btn-hover-lift" data-testid="continue-learning-btn">
                        <Play className="w-5 h-5 mr-2" />
                        Continue Learning
                      </Button>
                    </Link>
                  ) : (
                    <Button className="w-full bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full py-6 text-lg btn-hover-lift" data-testid="download-btn">
                      <Download className="w-5 h-5 mr-2" />
                      Download Now
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <Button 
                    onClick={handleBuyNow}
                    disabled={buyNowLoading}
                    className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-[#1A202C] rounded-full py-6 text-lg font-semibold btn-hover-lift"
                    data-testid="buy-now-btn"
                  >
                    {buyNowLoading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-[#1A202C]/30 border-t-[#1A202C] rounded-full animate-spin"></div>
                        Processing...
                      </span>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Buy Now - ${product?.price?.toFixed(2)}
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={handleAddToCart}
                    variant="outline"
                    className="w-full rounded-full py-6 text-lg"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                </div>
              )}

              {/* Features */}
              <div className="mt-6 pt-6 border-t border-[#E2E8F0] space-y-3">
                <div className="flex items-center gap-3 text-sm text-[#64748B]">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Lifetime access
                </div>
                <div className="flex items-center gap-3 text-sm text-[#64748B]">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Free future updates
                </div>
                <div className="flex items-center gap-3 text-sm text-[#64748B]">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  30-day money-back guarantee
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16">
          <Tabs defaultValue="reviews" className="w-full">
            <TabsList className="mb-8">
              <TabsTrigger value="reviews">Reviews ({reviews.length})</TabsTrigger>
              <TabsTrigger value="faq">FAQ</TabsTrigger>
            </TabsList>

            <TabsContent value="reviews" className="space-y-8">
              {/* Write Review (if purchased) */}
              {isPurchased && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
                  <h3 className="font-semibold text-[#1A202C] mb-4">Write a Review</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-[#64748B] mb-2 block">Rating</label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            onClick={() => setReviewRating(star)}
                            className="p-1"
                          >
                            <Star
                              className={`w-6 h-6 ${star <= reviewRating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#E2E8F0]'}`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Share your experience..."
                      className="w-full p-4 border border-[#E2E8F0] rounded-lg focus:border-[#2D4A5E] focus:outline-none min-h-[120px]"
                      data-testid="review-textarea"
                    />
                    <Button 
                      onClick={handleSubmitReview}
                      disabled={submittingReview}
                      className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full"
                      data-testid="submit-review-btn"
                    >
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Reviews List */}
              {reviews.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl">
                  <Award className="w-12 h-12 mx-auto text-[#CBD5E1] mb-4" />
                  <p className="text-[#64748B]">No reviews yet. Be the first to review!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.map(review => (
                    <div key={review.review_id} className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-medium text-[#1A202C]">{review.user_name}</div>
                          <div className="flex items-center gap-1 mt-1">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${star <= review.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#E2E8F0]'}`}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-sm text-[#64748B]">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-[#64748B]">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="faq">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0]">
                <div className="space-y-6">
                  {[
                    { q: 'How long do I have access?', a: 'You have lifetime access to this content once purchased.' },
                    { q: 'Is there a refund policy?', a: 'Yes, we offer a 30-day money-back guarantee if you\'re not satisfied.' },
                    { q: 'Can I download the content?', a: isCourse ? 'Video courses are streamed for the best experience. Supplementary materials can be downloaded.' : 'Yes, you can download and keep the files forever.' },
                    { q: 'Will I get future updates?', a: 'Yes, any updates to this content are included at no extra cost.' }
                  ].map((faq, i) => (
                    <div key={i}>
                      <h4 className="font-medium text-[#1A202C] mb-2">{faq.q}</h4>
                      <p className="text-[#64748B]">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
